import React, { useMemo, useState } from 'react';

import { Box, CircularProgress, MenuItem, MenuList, Tooltip, Typography, useTheme } from '@material-ui/core';
import type { GridColDef, GridSortModel } from '@mui/x-data-grid';
import { AiOutlineEye } from 'react-icons/ai';
import { FiCopy, FiFolder, FiMessageCircle, FiSettings } from 'react-icons/fi';
import { HiOutlineTrash } from 'react-icons/hi';
import { PiSparkleBold } from 'react-icons/pi';
import { RiShareForwardLine } from 'react-icons/ri';
import { TbListSearch, TbPencil } from 'react-icons/tb';

import {
  AppDataGridWithSavedPage,
  AppIconButton,
  AppLink,
  DetailsIcon,
  FormattedDescription,
  GridActions,
} from '@vyce/core/src/components';
import { FormDoc, FormRecipientsDialogType } from '@vyce/core/src/types';
import { buildFormUrlSegment, getUKFormattedDate } from '@vyce/core/src/utils';

import { DraggableFormTitleCell } from './DraggableFormTitleCell';
import { FormProgress } from './FormProgress';

interface Props {
  rows: FormDoc[];
  loading: boolean;
  total: number;
  height: string;
  setOffset: (offset: number) => void;
  enableDragAndDrop?: boolean;
  dragType?: string;
  withActions?: boolean;
  onEditForm?: (form: FormDoc) => void;
  onPreviewForm?: (form: FormDoc) => void;
  onDuplicateForm?: (form: FormDoc) => Promise<void> | void;
  onSettingsForm?: (form: FormDoc) => void;
  onSendForm?: (form: FormDoc) => void;
  onArchiveForm?: (form: FormDoc) => void;
  onViewAnswers?: (form: FormDoc) => void;
  onDeleteForm?: (form: FormDoc) => void;
  onChangeFolder?: (form: FormDoc) => void;
  onViewOutstanding?: (form: FormDoc) => void;
  onViewParticipants?: (form: FormDoc, type: FormRecipientsDialogType) => void;
  onResendAll?: (form: FormDoc) => void;
  onOpenAIAnalytics?: (form: FormDoc) => void;
  sortModel?: GridSortModel;
  onSortModelChange?: (model: GridSortModel) => void;
  onPageChange?: (page: number) => void;
  pageSize?: number;
  rowsPerPageOptions?: number[];
}

export const FormsDataGrid: React.FC<Props> = ({
  rows,
  loading,
  total,
  height,
  setOffset,
  enableDragAndDrop = false,
  dragType,
  withActions = false,
  onEditForm,
  onPreviewForm,
  onDuplicateForm,
  onSettingsForm,
  onSendForm,
  onArchiveForm,
  onViewAnswers,
  onDeleteForm,
  onChangeFolder,
  onViewOutstanding,
  onViewParticipants,
  onResendAll,
  onOpenAIAnalytics,
  sortModel,
  onSortModelChange,
  onPageChange,
  pageSize,
  rowsPerPageOptions,
}) => {
  const [closeGridAction, setCloseGridAction] = useState(false);
  const [loadingDuplicateForm, setLoadingDuplicateForm] = useState(false);
  const theme = useTheme();

  const resetCloseStatus = () => {
    setCloseGridAction(true);
    setTimeout(() => setCloseGridAction(false), 100);
  };

  const participantTooltips: Record<FormRecipientsDialogType, string> = {
    recipients: 'View recipients',
    responses: 'View completed',
    outstanding: 'View outstanding',
  };

  const renderCountCell = (value: number | null, type: FormRecipientsDialogType, form: FormDoc) => {
    if (value == null || isNaN(value)) {
      return <Typography style={{ fontWeight: 500, fontSize: 13 }}>-</Typography>;
    }
    if (!onViewParticipants) {
      return <Typography style={{ fontWeight: 500, fontSize: 13 }}>{value}</Typography>;
    }
    return (
      <Tooltip title={participantTooltips[type]} arrow>
        <Typography
          onClick={() => onViewParticipants(form, type)}
          style={{
            fontWeight: 600,
            fontSize: 13,
            color: theme.palette.secondary.main,
            textDecoration: 'underline',
            cursor: 'pointer',
            display: 'inline-block',
          }}
        >
          {value}
        </Typography>
      </Tooltip>
    );
  };

  const actionsColumn: GridColDef = useMemo(
    () => ({
      field: '',
      headerName: 'Actions',
      width: 100,
      hideSortIcons: true,
      sortable: false,
      disableColumnMenu: true,
      renderCell: params => {
        const form = params.row as FormDoc;
        const isPublished = form.status === 'published';
        return (
          <Box display="flex" width="100%" alignItems="center" justifyContent="flex-end" gridGap={8}>
            {isPublished && onOpenAIAnalytics && (
              <Box display="flex" alignItems="center" gridGap={8}>
                {onOpenAIAnalytics && (
                  <AppIconButton
                    variant="default"
                    disableFocusRipple
                    disableRipple
                    disableTouchRipple
                    onClick={() => {
                      resetCloseStatus();
                      onOpenAIAnalytics(form);
                    }}
                  >
                    <PiSparkleBold size={21} />
                  </AppIconButton>
                )}
              </Box>
            )}

            <GridActions close={closeGridAction}>
              <MenuList>
                {form.status === 'draft' && onEditForm && (
                  <MenuItem
                    onClick={() => {
                      resetCloseStatus();
                      onEditForm(form);
                    }}
                  >
                    <Box display="flex" alignItems="center" gridGap={8}>
                      <TbPencil size={14} /> Edit form
                    </Box>
                  </MenuItem>
                )}

                {onPreviewForm && (
                  <MenuItem
                    onClick={() => {
                      resetCloseStatus();
                      onPreviewForm(form);
                    }}
                  >
                    <Box display="flex" alignItems="center" gridGap={8}>
                      <AiOutlineEye size={14} /> Preview form
                    </Box>
                  </MenuItem>
                )}

                {onDuplicateForm && (
                  <MenuItem
                    onClick={async () => {
                      resetCloseStatus();
                      setLoadingDuplicateForm(true);
                      await onDuplicateForm(form);
                      setLoadingDuplicateForm(false);
                    }}
                  >
                    <Box display="flex" alignItems="center" gridGap={8}>
                      {loadingDuplicateForm ? <CircularProgress size={14} /> : <FiCopy size={14} />}
                      Duplicate form
                    </Box>
                  </MenuItem>
                )}

                {isPublished && onSettingsForm && (
                  <MenuItem
                    onClick={() => {
                      resetCloseStatus();
                      onSettingsForm(form);
                    }}
                  >
                    <Box display="flex" alignItems="center" gridGap={8}>
                      <FiSettings size={14} /> Settings
                    </Box>
                  </MenuItem>
                )}

                {isPublished && onSendForm && (
                  <MenuItem
                    onClick={() => {
                      resetCloseStatus();
                      onSendForm(form);
                    }}
                  >
                    <Box display="flex" alignItems="center" gridGap={8}>
                      <RiShareForwardLine size={14} /> Send form
                    </Box>
                  </MenuItem>
                )}

                {isPublished && onViewOutstanding && (
                  <MenuItem
                    onClick={() => {
                      resetCloseStatus();
                      onViewOutstanding(form);
                    }}
                  >
                    <Box display="flex" alignItems="center" gridGap={8}>
                      <TbListSearch size={14} /> View outstanding
                    </Box>
                  </MenuItem>
                )}

                {isPublished && onResendAll && (
                  <MenuItem
                    onClick={() => {
                      resetCloseStatus();
                      onResendAll(form);
                    }}
                  >
                    <Box display="flex" alignItems="center" gridGap={8}>
                      <FiMessageCircle size={14} /> Resend all
                    </Box>
                  </MenuItem>
                )}

                {onViewAnswers && isPublished && (
                  <MenuItem
                    onClick={() => {
                      resetCloseStatus();
                      onViewAnswers(form);
                    }}
                  >
                    <Box display="flex" alignItems="center" gridGap={8}>
                      <TbListSearch size={14} /> View answers
                    </Box>
                  </MenuItem>
                )}

                {onChangeFolder && isPublished && (
                  <MenuItem
                    onClick={() => {
                      resetCloseStatus();
                      onChangeFolder(form);
                    }}
                  >
                    <Box display="flex" alignItems="center" gridGap={8}>
                      <FiFolder size={14} /> Change folder
                    </Box>
                  </MenuItem>
                )}

                {isPublished && onArchiveForm && (
                  <MenuItem
                    onClick={() => {
                      resetCloseStatus();
                      onArchiveForm(form);
                    }}
                  >
                    <Box display="flex" alignItems="center" gridGap={8}>
                      <HiOutlineTrash size={14} /> Archive form
                    </Box>
                  </MenuItem>
                )}

                {onDeleteForm && (
                  <MenuItem
                    onClick={() => {
                      resetCloseStatus();
                      onDeleteForm(form);
                    }}
                  >
                    <Box display="flex" alignItems="center" gridGap={8}>
                      <HiOutlineTrash size={14} /> Delete form
                    </Box>
                  </MenuItem>
                )}
              </MenuList>
            </GridActions>
          </Box>
        );
      },
    }),
    [
      closeGridAction,
      loadingDuplicateForm,
      onArchiveForm,
      onDeleteForm,
      onChangeFolder,
      onDuplicateForm,
      onEditForm,
      onOpenAIAnalytics,
      onPreviewForm,
      onResendAll,
      onSendForm,
      onSettingsForm,
      onViewAnswers,
      onViewOutstanding,
    ]
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'title',
        headerName: 'Form Title',
        flex: 0.2,
        minWidth: 170,
        disableColumnMenu: true,
        renderCell: params => {
          if (enableDragAndDrop && dragType) {
            return (
              <DraggableFormTitleCell
                form={params.row as FormDoc}
                title={params.row?.title}
                dragType={dragType}
              />
            );
          }
          return (
            <Box display="flex" width="100%" gridGap={8} justifyContent="space-between" alignItems="center">
              <Box
                flexGrow={1}
                minWidth={0}
                style={{
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  lineHeight: 1.4,
                }}
              >
                <AppLink
                  to={
                    params.row.status === 'draft'
                      ? `/forms/my-forms/${buildFormUrlSegment(params.row.title, params.row.id)}`
                      : `/forms/form-answers/${buildFormUrlSegment(params.row.title, params.row.id)}/summary`
                  }
                >
                  {params.row?.title}
                </AppLink>
              </Box>

              {params.row.description && (
                <DetailsIcon
                  content={<FormattedDescription text={params.row.description} variant="caption" />}
                  type="info"
                  richContent
                />
              )}
            </Box>
          );
        },
      },
      {
        field: 'folderName',
        headerName: 'Folder',
        flex: 0.12,
        width: 160,
        sortable: false,
        disableColumnMenu: true,
        renderCell: params => {
          const folderName = params.row.folderName;
          const folderId = params.row.folderId;
          if (!folderName || !folderId) {
            return <Typography style={{ fontWeight: 500, fontSize: 13 }}>-</Typography>;
          }

          return (
            <AppLink to={`/forms/my-forms/folders/${buildFormUrlSegment(folderName, folderId)}`}>
              {folderName}
            </AppLink>
          );
        },
      },
      {
        field: 'sendTo',
        headerName: 'Sent To',
        flex: 0.1,
        width: 95,
        sortable: false,
        disableColumnMenu: true,
        renderCell: params =>
          renderCountCell(params.row.sendTo, 'recipients', params.row as FormDoc),
      },
      {
        field: 'filled',
        headerName: 'Completed',
        flex: 0.1,
        width: 95,
        sortable: false,
        disableColumnMenu: true,
        renderCell: params => renderCountCell(params.row.filled, 'responses', params.row as FormDoc),
      },
      {
        field: 'outstanding',
        headerName: 'Outstanding',
        flex: 0.1,
        width: 95,
        sortable: false,
        disableColumnMenu: true,
        renderCell: params => {
          const { sendTo, filled } = params.row;
          const outstanding = isNaN(sendTo) || isNaN(filled) ? null : Math.max(sendTo - filled, 0);
          return renderCountCell(outstanding, 'outstanding', params.row as FormDoc);
        },
      },
      {
        field: 'completionRate',
        headerName: 'Completion rate',
        flex: 0.15,
        width: 200,
        sortable: false,
        disableColumnMenu: true,
        renderCell: params => {
          const isDraft = params.row.status === 'draft';
          const completionRate =
            !isDraft && !isNaN(params.row.sendTo) && !isNaN(params.row.filled)
              ? Math.round((params.row.filled / params.row.sendTo) * 100)
              : 0;
          return (
            <Box display="flex" width="100%" alignItems="center" gridGap={8}>
              <Tooltip title={`${completionRate}%`}>
                <Box width="100%">
                  <FormProgress completionRate={completionRate || 0} />
                </Box>
              </Tooltip>
            </Box>
          );
        },
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: 0.1,
        width: 150,
        disableColumnMenu: true,
        renderCell: params => (
          <Typography style={{ fontWeight: 500, fontSize: 13 }}>{params.row.status || '-'}</Typography>
        ),
      },
      {
        field: 'updatedAt',
        headerName: 'Updated at',
        flex: 0.1,
        width: 150,
        disableColumnMenu: true,
        renderCell: params => (
          <Typography style={{ fontWeight: 500, fontSize: 13 }}>
            {params.row.updatedAt?._seconds != null
              ? getUKFormattedDate(params.row.updatedAt._seconds * 1000)
              : '-'}
          </Typography>
        ),
      },
      ...(withActions ? [actionsColumn] : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [actionsColumn, dragType, enableDragAndDrop, withActions, onViewParticipants, theme]
  );

  return (
    <AppDataGridWithSavedPage
      rows={rows}
      getRowId={row => row.id}
      columns={columns}
      noPaper
      rowHeight={88}
      paginationMode="server"
      sortingMode="server"
      height={height}
      loading={loading}
      rowCount={total}
      sortModel={sortModel}
      onSortModelChange={onSortModelChange}
      pageSize={pageSize}
      rowsPerPageOptions={rowsPerPageOptions}
      onPageChange={onPageChange}
      disableSelectionOnClick
      setOffset={setOffset}
    />
  );
};
